import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  FactoryProvider,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withRouterConfig } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { EffectsModule } from '@ngrx/effects';
import { provideStore, StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app/app.component';
import { TENANT_INTERCEPTOR_PROVIDERS } from './app/shared/interceptors/tenant-interceptor.providers';

import { provideServiceWorker } from '@angular/service-worker';
import { JwtModule } from '@auth0/angular-jwt';
import config from 'devextreme/core/config';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { appRoutes } from './app/app-routing';
import { GraphService } from './app/auth/graph.service';
import { InitialStartupService } from './app/auth/initial-startup.service';
import { MsalConfig } from './app/auth/msal.config';
import { licenseKey } from './app/devextreme-key';
import { ClickCorrelationInterceptorProvider } from './app/shared/interceptors/click-correlation.interceptor';
import { ErrorInterceptorProvider } from './app/shared/interceptors/error.interceptor';
import { LoaderInterceptorProvider } from './app/shared/interceptors/loader.interceptor';
import { LoadingSpinnerComponent } from './app/shared/loading-spinner/loading-spinner.component';
import { Identifier } from './app/shared/models/storage.models';
import { clinicsReducer } from './app/store/clinics/clinics.reducer';
import { initialDataReducer } from './app/store/initalData/initial-data.reducer';
import { uiReducer } from './app/store/ui/ui.reducer';
import { UserEffects } from './app/store/user/user.effects';
import { userReducer } from './app/store/user/user.reducer';
//import { SharedDynamicModule } from './app/shared/dynamic/dynamic.module';

config({ licenseKey });
export class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private resources: {
      prefix: string;
      suffix: string;
    }[]
  ) {}

  public getTranslation(lang: string): Observable<any> {
    //const headers = new HttpHeaders().set(InterceptorSkipHeader, '');

    const requests = this.resources.map((resource) => {
      const path = resource.prefix + lang + resource.suffix;
      return this.http.get(path /*, { headers }*/).pipe(
        catchError((res) => {
          console.error('Something went wrong for the following translation file:', path);
          console.error(res.message);
          return of({});
        })
      );
    });

    return forkJoin(requests).pipe(
      map((responses) => Object.assign({}, ...responses)) // Merge all responses into one object
    );
  }
}
export function httpLoaderFactory(httpHandler: HttpBackend): any {
  return new MultiTranslateHttpLoader(new HttpClient(httpHandler), [{ prefix: './assets/i18n/', suffix: '.json' }]);
}
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
export function appInitializerFn(configService: InitialStartupService) {
  return () => configService.initApplication();
}
export function msalInstanceFactory(appConfigService: InitialStartupService): IPublicClientApplication {
  if (appConfigService.isSSOLogin) {
    return MSALInstanceFactory();
  } else {
    return FakeMSALInstanceFactory();
  }
}
export function loggerCallback(logLevel: LogLevel, message: string): void {
  //console.log(message);
}
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: MsalConfig.clientId,
      authority: MsalConfig.authority,
      redirectUri: MsalConfig.redirectUri,
      postLogoutRedirectUri: MsalConfig.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loadFrameTimeout: 30000,
      tokenRenewalOffsetSeconds: 60,
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        piiLoggingEnabled: false,
        logLevel: LogLevel.Verbose,
      },
    },
  });
}
export function FakeMSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '',
      authority: '',
      redirectUri: '',
      postLogoutRedirectUri: '',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loadFrameTimeout: 30000,
      tokenRenewalOffsetSeconds: 60,
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        piiLoggingEnabled: false,
        logLevel: LogLevel.Verbose,
      },
    },
  });
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap: Map<string, string[]> = new Map<string, string[]>();
  protectedResourceMap.set(MsalConfig.redirectUri, MsalConfig.loginRequest.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...MsalConfig.loginRequest.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

const msalProvider: FactoryProvider = {
  provide: MSAL_INSTANCE,
  useFactory: msalInstanceFactory,
  deps: [InitialStartupService],
};

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      LoadingSpinnerComponent,
      TranslateService,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpBackend],
        },
        extend: true,
      }),
      JwtModule.forRoot({
        config: {
          tokenGetter: () => localStorage.getItem(Identifier.TYPE_MSAL_ID_TOKEN),
        },
      }),
      StoreModule.forRoot({
        ui: uiReducer,
        user: userReducer,
        clinics: clinicsReducer,
        initalData: initialDataReducer,
      }),
      EffectsModule.forRoot([UserEffects])
    ),
    provideStore(
      {
        ui: uiReducer,
        user: userReducer,
        clinics: clinicsReducer,
        initalData: initialDataReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionWithinNgZone: true,
        },
      }
    ),
    ErrorInterceptorProvider,
    LoaderInterceptorProvider,
    ClickCorrelationInterceptorProvider,
    // AuthGuard,
    // ReferenceTablesHttpService,
    // Auth -> ClinicContext -> AppHttp. The order is load-bearing and lives in one place
    // (AB#92762); `interceptor-chain.spec.ts` imports the same array, so it is under test.
    ...TENANT_INTERCEPTOR_PROVIDERS,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    msalProvider,
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    GraphService,
    InitialStartupService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [InitialStartupService],
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      appRoutes,
      //withPreloading(QuicklinkStrategy),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
}).catch((err) => console.error(err));
